import {
  Button,
  Box,
  Stack,
  Text,
  Divider,
  Input,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
} from '@ltvco/refresh-lib/theme';
import { useStatesOptionsAbbr } from '@ltvco/refresh-lib/utils';
import { RapidsInput, RapidsSearchData } from '@ltvco/refresh-lib/v1';
import { AdvancedSearchURLParams } from '@ltvco/refresh-lib/v2';
import CloseIcon from '@mui/icons-material/Close';
import { SetStateAction, Dispatch } from 'react';

import { ReportColumnContainer } from 'components/ChatBV/components';
import { monthOptions } from 'routes/SearchPerson/SearchFilter/constants';

type FiltersColumnProps = {
  currentWidth: string;
  isMobileBreakpoint: boolean;
  onClose: () => void;
  searchParams: AdvancedSearchURLParams;
  setSearchParams: Dispatch<SetStateAction<AdvancedSearchURLParams>>;
  handleOpenTeaser: () => void;
};

const RapidsInputStyle = {
  '& > .MuiAutocomplete-root': {
    width: '100%',
    '& > .address-input-container': {
      '& > .address-input': {
        width: '100%',
        '& > .rapids-search-icon': {
          display: 'none',
        },
      },
    },
  },
};

export const FiltersColumn = ({
  currentWidth,
  isMobileBreakpoint,
  onClose,
  searchParams,
  setSearchParams,
  handleOpenTeaser,
}: FiltersColumnProps) => {
  const states = Object.values(useStatesOptionsAbbr())
    .map((state) => state.display)
    .filter((state) => state !== 'All');

  const handleSubmit = () => {
    handleOpenTeaser();
  };

  const handleSearchLocation = (propertyData: RapidsSearchData) => {
    const { city, state } = propertyData;
    const data = {
      city,
      state,
    };
    setSearchParams({ ...searchParams, ...data });
  };

  const handleStateSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchParams({ ...searchParams, state: value });
  };

  const handleMonthSelect = (value: string, type: 'birth' | 'death') => {
    setSearchParams({
      ...searchParams,
      [type === 'birth' ? 'birthMonth' : 'deathMonth']: value,
    });
  };

  return (
    <ReportColumnContainer
      spacing={2}
      minWidth={currentWidth}
      width="40%"
      height="98%"
      sx={{
        px: 4,

        ...(isMobileBreakpoint && {
          animation: 'fadeInScale 0.4s ease-out',
          '@keyframes fadeInScale': {
            '0%': {
              transform: 'scale(0.95)',
              opacity: 0,
            },
            '100%': {
              transform: 'scale(1)',
              opacity: 1,
            },
          },
        }),
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.paper',
          zIndex: 100,
          padding: '16px 20px',
        }}
      >
        <Text variant="body1" fontWeight="bold" color="text.primary">
          Filters
        </Text>
        <CloseIcon
          onClick={onClose}
          sx={{ color: 'text.secondary', cursor: 'pointer' }}
        />
      </Stack>
      <Divider />
      <Stack spacing={3} sx={{ minHeight: 'calc(100vh - 196px)' }}>
        <Text variant="body1" fontWeight="bold" color="text.primary">
          Name Search
        </Text>
        <Input
          fullWidth
          autoFocus={false}
          value={searchParams.fullName}
          label="Full Name"
          data-cy="full_name_field"
          slotProps={{
            input: {
              sx: {
                borderRadius: '4px',
              },
            },
          }}
          onChange={(e) =>
            setSearchParams({ ...searchParams, fullName: e.target.value })
          }
          variant="outlined"
        />
        <Text variant="body1" fontWeight="bold" color="text.primary">
          Location
        </Text>
        <Stack direction="row" spacing={2}>
          <RapidsInput
            parentFormSubmitHandler={handleSearchLocation}
            searchTypes={['city']}
            includeCounties={false}
            updateEmptyOptions
            valueOveride={searchParams.city}
            inputOptions={{
              placeholder: 'City',
              hideSearchButton: true,
              hideInputLabel: true,
              InputProps: {
                sx: {
                  borderRadius: '4px',
                },
              },
              autoFocus: false,
              customStyles: RapidsInputStyle,
            }}
            disableCloseOnSelect={true}
            data-cy="city_field"
            inputLabel="City"
          />
          <Autocomplete
            id="state"
            data-cy="state_header_field"
            options={states}
            value={searchParams.state}
            renderInput={(params) => (
              <Input
                {...params}
                label="State"
                variant="outlined"
                name="state"
                data-cy="state_report_field"
                onInput={handleStateSelect}
                onChange={handleStateSelect}
              />
            )}
            onChange={(_event, data) =>
              setSearchParams({ ...searchParams, state: data })
            }
            isOptionEqualToValue={(option, optionValue) =>
              optionValue === '' || option === optionValue
            }
          />
        </Stack>
        <Text variant="body1" fontWeight="bold" color="text.primary">
          Age
        </Text>
        <Stack direction="row" spacing={2}>
          <FormControl sx={{ width: '50%' }}>
            <InputLabel
              id="birthMonth"
              shrink={Boolean(searchParams.birthMonth)}
              sx={{ top: searchParams.birthMonth ? '0px' : '-7px' }}
            >
              Birth Month
            </InputLabel>
            <Select
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-notchedOutline legend': {
                  width: searchParams.birthMonth ? 'auto' : '0px',
                  overflow: 'hidden',
                },
              }}
              value={searchParams.birthMonth}
              options={monthOptions}
              id="birthMonth"
              data-cy="birth_month_select"
              MenuProps={{
                disablePortal: true,
              }}
              label="Birth Month"
              labelId="birthMonth"
              onOptionSelect={(value) => handleMonthSelect(value, 'birth')}
            />
          </FormControl>
          <Input
            placeholder="Year"
            sx={{ width: '50%' }}
            type="number"
            label="Year"
            value={searchParams.birthYear}
            onChange={(e) =>
              setSearchParams({
                ...searchParams,
                birthYear: e?.target?.value || '',
              })
            }
          />
        </Stack>
        <Stack direction="row" spacing={2}>
          <FormControl sx={{ width: '50%' }}>
            <InputLabel
              id="deathMonth"
              shrink={Boolean(searchParams.deathMonth)}
              sx={{ top: searchParams.deathMonth ? '0px' : '-7px' }}
            >
              Death Month
            </InputLabel>
            <Select
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-notchedOutline legend': {
                  width: searchParams.deathMonth ? 'auto' : '0px',
                  overflow: 'hidden',
                },
              }}
              value={searchParams.deathMonth}
              options={monthOptions}
              id="deathMonth"
              data-cy="death_month_select"
              MenuProps={{
                disablePortal: true,
              }}
              label="Death Month"
              labelId="deathMonth"
              onOptionSelect={(value) => handleMonthSelect(value, 'death')}
            />
          </FormControl>
          <Input
            placeholder="Year"
            sx={{ width: '50%' }}
            type="number"
            label="Year"
            value={searchParams.deathYear}
            onChange={(e) =>
              setSearchParams({
                ...searchParams,
                deathYear: e?.target?.value || '',
              })
            }
          />
        </Stack>
        {/* <Text variant="body1" fontWeight="bold" color="text.primary">
          Professional Search
        </Text>
        <Input
          fullWidth
          autoFocus={false}
          value={searchParams.title}
          label="Job Title"
          data-cy="title_field"
          slotProps={{
            input: {
              sx: {
                borderRadius: '4px',
              },
            },
          }}
          onChange={(e) =>
            setSearchParams({ ...searchParams, title: e.target.value })
          }
          variant="outlined"
        />
        <Input
          fullWidth
          autoFocus={false}
          value={searchParams.company}
          label="Company"
          data-cy="company_field"
          slotProps={{
            input: {
              sx: {
                borderRadius: '4px',
              },
            },
          }}
          onChange={(e) =>
            setSearchParams({ ...searchParams, company: e.target.value })
          }
          variant="outlined"
        />
        <Autocomplete
          fullWidth
          autoFocus={false}
          value={searchParams.industry}
          options={contactIndustries}
          data-cy="industry_field"
          renderInput={(params) => (
            <Input {...params} label="Industry" variant="outlined" />
          )}
          onChange={(_event, data) =>
            setSearchParams({ ...searchParams, industry: data })
          }
          isOptionEqualToValue={(option, optionValue) =>
            optionValue === '' || option === optionValue
          }
        />
        <Text variant="body1" fontWeight="bold" color="text.primary">
          Seniority
        </Text>
        <Stack>
          {contactSeniorityLevels.map((level) => (
            <FormControlLabel
              key={level.value}
              control={
                <Checkbox
                  checked={searchParams.level.includes(level.value)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const newLevels = [...searchParams.level, level.value];
                      setSearchParams({ ...searchParams, level: newLevels });
                    } else {
                      const newLevels = searchParams.level.filter(
                        (l: string) => l !== level.value
                      );
                      setSearchParams({ ...searchParams, level: newLevels });
                    }
                  }}
                />
              }
              label={level.label}
            />
          ))}
        </Stack>
        <Text variant="body1" fontWeight="bold" color="text.primary">
          Employee Count
        </Text>
        <Stack>
          <Slider
            getAriaLabel={(index) =>
              index === 0 ? 'Min Employee Count' : 'Max Employee Count'
            }
            name="employee-count"
            data-cy="employee_count_slider"
            min={1}
            max={9}
            value={[
              searchParams.company_size_min
                ? Number.parseInt(searchParams.company_size_min, 10)
                : 1,
              searchParams.company_size_max
                ? Number.parseInt(searchParams.company_size_max, 10)
                : 9,
            ]}
            valueLabelDisplay="auto"
            marks={contactEmployeeCountRanges.map((range) => ({
              value: range.value,
            }))}
            step={null}
            onChange={(_, value) => {
              if (Array.isArray(value)) {
                setSearchParams({
                  ...searchParams,
                  company_size_min: value[0]?.toString() ?? '',
                  company_size_max: value[1]?.toString() ?? '',
                });
              }
            }}
            valueLabelFormat={(value: number) => findEmployeeCountLabel(value)}
          />
          <Text
            variant="caption"
            sx={{ textAlign: 'center' }}
            data-cy="employee_count_label"
          >
            Min {findEmployeeCountLabel(searchParams.company_size_min)} - Max{' '}
            {findEmployeeCountLabel(searchParams.company_size_max)}
          </Text>
        </Stack> */}
      </Stack>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit}>
          Show Results
        </Button>
      </Box>
    </ReportColumnContainer>
  );
};
